import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, FileText, MapPinned } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { api } from '../lib/api';

/* Header site search (desktop + mobile).
   Searches two things only, both privacy-safe:
   1. Public site pages (from the nav menu configuration)
   2. PUBLIC issue reports (consent given) with public-safe fields only:
      public title, general area, category, status badge, tracking number.
   No private resident details are ever requested or shown. */

// Status badge styling matches the public map legend colours
const STAGE_BADGES = {
  open: { label: 'Open', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  in_progress: { label: 'In Progress', cls: 'bg-amber-50 text-[#B54708] border-amber-200' },
  awaiting_city_response: { label: 'Awaiting City Response', cls: 'bg-violet-50 text-[#7c3aed] border-violet-200' },
  resolved: { label: 'Resolved', cls: 'bg-emerald-50 text-[#067647] border-emerald-200' },
};

const categoryLabel = (c) => (c || 'other').replaceAll('_', ' ').replace(/\b\w/g, (m) => m.toUpperCase());

export const SiteSearch = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ pages: [], issues: [] });
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  // Ctrl/Cmd + K opens search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Reset when the dialog closes; focus the input when it opens
  useEffect(() => {
    if (!open) { setQ(''); setResults({ pages: [], issues: [] }); setSearched(false); return undefined; }
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open]);

  // Debounced server search
  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) { setResults({ pages: [], issues: [] }); setSearched(false); setLoading(false); return undefined; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get('/search', { params: { q: query } })
        .then((r) => setResults({ pages: r.data?.pages || [], issues: r.data?.issues || [] }))
        .catch(() => setResults({ pages: [], issues: [] }))
        .finally(() => { setLoading(false); setSearched(true); });
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (url) => { setOpen(false); navigate(url); };
  const hasResults = results.pages.length > 0 || results.issues.length > 0;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="min-w-[44px] min-h-[44px] p-2.5 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors duration-150"
        aria-label="Search the site"
        data-testid="site-search-button"
      >
        <Search className="h-5 w-5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="p-0 gap-0 max-w-xl w-[calc(100vw-2rem)] rounded-xl overflow-hidden" data-testid="site-search-dialog">
          <DialogTitle className="sr-only">Search the site</DialogTitle>
          <div className="flex items-center gap-2 border-b border-slate-200 px-4">
            <Search className="h-4 w-4 text-slate-400 shrink-0" aria-hidden="true" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search pages or public Ward 8 issue reports…"
              className="flex-1 h-12 bg-transparent text-[15px] text-[#1a2744] placeholder:text-slate-400 outline-none min-w-0"
              aria-label="Search pages or public issue reports"
              data-testid="site-search-input"
            />
            {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400 shrink-0" />}
          </div>

          <div className="max-h-[60vh] overflow-y-auto" data-testid="site-search-results">
            {q.trim().length < 2 && (
              <p className="px-4 py-8 text-sm text-slate-500 text-center" data-testid="site-search-hint">
                Type at least 2 letters to search pages and public issue reports.
              </p>
            )}

            {q.trim().length >= 2 && searched && !loading && !hasResults && (
              <p className="px-4 py-8 text-sm text-slate-500 text-center" data-testid="site-search-empty">
                No matches for “{q.trim()}”. Try a different word, a community name, or a W8 tracking number.
              </p>
            )}

            {results.pages.length > 0 && (
              <div className="py-2" data-testid="site-search-pages">
                <p className="px-4 pb-1 text-[11px] font-black uppercase tracking-wider text-slate-400">Pages</p>
                {results.pages.map((p, i) => (
                  <button
                    key={p.url + i}
                    onClick={() => go(p.url)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 min-h-[44px] text-left hover:bg-slate-50 focus-visible:bg-slate-50 outline-none"
                    data-testid={`site-search-page-${i}`}
                  >
                    <FileText className="h-4 w-4 text-[#8a6d2c] shrink-0" aria-hidden="true" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-bold text-[#1a2744] truncate">{p.label}</span>
                      <span className="block text-xs text-slate-500 truncate">{p.group_label}{p.description ? ` · ${p.description}` : ''}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {results.issues.length > 0 && (
              <div className="py-2 border-t border-slate-100" data-testid="site-search-issues">
                <p className="px-4 pb-1 text-[11px] font-black uppercase tracking-wider text-slate-400">Public Issue Reports</p>
                {results.issues.map((it) => {
                  const badge = STAGE_BADGES[it.stage] || STAGE_BADGES.open;
                  return (
                    <button
                      key={it.tracking_number}
                      onClick={() => go(`/issue/${it.tracking_number}`)}
                      className="w-full flex items-start gap-3 px-4 py-2.5 min-h-[44px] text-left hover:bg-slate-50 focus-visible:bg-slate-50 outline-none"
                      data-testid={`site-search-issue-${it.tracking_number}`}
                    >
                      <MapPinned className="h-4 w-4 text-[#8a6d2c] shrink-0 mt-0.5" aria-hidden="true" />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-bold text-[#1a2744] truncate">{it.title || 'Ward 8 issue report'}</span>
                        <span className="block text-xs text-slate-500 truncate">
                          {[it.tracking_number, categoryLabel(it.category), it.area].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                      <Badge variant="outline" className={`shrink-0 text-[10px] font-bold ${badge.cls}`} data-testid={`site-search-issue-status-${it.tracking_number}`}>
                        {badge.label}
                      </Badge>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <p className="px-4 py-2.5 border-t border-slate-100 text-[11px] text-slate-400" data-testid="site-search-privacy-note">
            Searches public pages and public issue reports only. Private resident details never appear.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};
